import { DataNode } from 'antd/lib/tree';
import { FailureSummary } from './enforcement-flow/failureSummary.type';

export type FieldValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | Record<string, unknown>
  | DataNode[];

export interface KeyValEnum {
  key: string;
  value: string;
  disabled?: boolean;
  picture?: string;
  email?: string;
  text?: string;
  hidePicture?: boolean;
}

export enum InputFieldType {
  Select = 'Select',
  String = 'String',
  Number = 'Number',
  Date = 'Date',
  Boolean = 'Boolean',
  Enum = 'Enum',
  Dictionary = 'Dictionary',
  ManagedDictionary = 'ManagedDictionary',
  File = 'File',
  UploadFile = 'UploadFile',
  FileInformation = 'FileInformation',
  Url = 'Url',
  MultiSelect = 'MultiSelect',
  TextArea = 'TextArea',
  Mentions = 'Mentions',
  Info = 'Info',
  TreeView = 'TreeView',
  FileDecisionTagging = 'FileDecisionTagging',
  Nested = 'Nested',
  Array = 'Array',
  JsonEditor = 'JsonEditor',
  RecoBatchTransferIds = 'Reco:BatchTransferIds',
  AlertMessage = 'AlertMessage',
  Button = 'Button',
  PreviewHtmlModal = 'PreviewHtmlModal'
}

export enum EnumDisplayType {
  List = 'List',
  Radio = 'Radio'
}

export enum DictionaryDisplayType {
  List = 'List',
  Radio = 'Radio',
  Checkbox = 'Checkbox'
}

export interface ControlActionField {
  id?: number;
  name: string;
  displayName?: string;
  description?: string;
  type: InputFieldType;
  isRequired?: boolean;
  isUnique?: boolean;
  enumsDisplayType?: EnumDisplayType;
  enums?: string[] | { label: string; value: string }[];
  useEnums?: (initialValue?: { value: string; label: string }[]) => {
    enums: { value: string; label: string }[];
    loading: boolean;
    disabled?: boolean;
  };
  tree?: DataNode[];
  dictionaryDisplayType?: DictionaryDisplayType;
  dictionary?: KeyValEnum[];
  useKeyAsLabel?: boolean;
  multiSelect?: boolean;
  multiSelectMode?: 'tags' | 'multiple';
  value?: FieldValue | FieldValue[];
  defaultValue?: FieldValue | FieldValue[];
  accept?: string[];
  bold?: boolean;
  delete?: boolean;
  placeholder?: string;
  disabled?: boolean;
  nestedFields?: ControlActionField[];
  innerField?: ControlActionField;
  isHidden?: boolean;
  optionDisabled?: boolean;
  mode?: string;
  dictionarySelectedOptionsRequiredLength?: number;
  dictionaryAllCheckboxesRequired?: boolean;
  lowerText?: string;
  label?: string;
  shouldFormatDisplayValues?: boolean;
  metadata?: Record<string, unknown>;
  helperText?: string;
  warningText?: string;
  informationText?: string;
  header?: string;
  searchByKeyAndValue?: boolean;
  allowClear?: boolean;
  spiiData?: {
    isSpii: boolean;
    partnerName?: string;
    useCryptoService?: boolean;
  };
  uiOptions?: {
    showFieldAndWidgetInline?: boolean;
  };
  collapseGroup?: CollapseTypes;
  collapseGroupDisplayName?: string;
  nestedCollapseGroup?: CollapseTypes;
  nestedCollapseGroupDisplayName?: string;
  isExpendByDefault?: boolean;
  fieldWidth?: string;
  isLastInRow?: boolean;
  collapseWidth?: string;
  direction?: DirectionTypes;
}

export type ControlActionInfo = {
  name: string;
  displayName?: string;
  value: string;
  type: InputFieldType.Info;
  bold?: boolean;
  isHidden?: boolean;
  metadata?: Record<string, unknown>;
  helperText?: string;
  warningText?: string;
  informationText?: string;
  header?: string;
  collapseGroup?: CollapseTypes;
  collapseGroupDisplayName?: string;
  nestedCollapseGroup?: CollapseTypes;
  nestedCollapseGroupDisplayName?: string;
  isExpendByDefault?: boolean;
  collapseWidth?: string;
};

export interface ActionFields {
  fields: ControlActionField[];
  fieldsGroup?: ControlActionGroup;
  refetch?: () => void;
  failureSummary?: FailureSummary[];
}
export interface ControlActionGroup {
  fieldsSchema: Record<string, ControlActionField>;
  fieldsValues: Group[];
  fieldsSettings?: {
    shouldHideRemove?: boolean;
    maxItems?: number;
    buttonText?: string;
    disableAutoInit?: boolean;
  };
}

export interface Group {
  delete?: boolean;
  index: number;
  values: Record<string, ControlActionField>;
}

export type ActionType = 'single' | 'bulk' | 'global';

export enum ActionableEntities {
  Task = 'task',
  BusinessVerificationTask = 'businessVerificationTask',
  Escalation = 'escalation',
  Organization = 'organization',
  Vendor = 'vendor',
  Payment = 'payment',
  FundingSource = 'fundingSource',
  DeliveryMethod = 'deliveryMethod',
  User = 'user',
  Project = 'project',
  Partner = 'partner',
  Adjustment = 'adjustment',
  Check = 'check',
  Card = 'card',
  Ach = 'ach',
  RejectCheck = 'rejectCheck',
  Unclassified = 'unclassified',
  MelioDbCase = 'melioDbCase',
  Chargeback = 'chargeback',
  UBO = 'ubo',
  Oki = 'oki',
  Ip = 'ip',
  PaymentIp = 'paymentIp',
  PayorIp = 'payorIp',
  PayeeIp = 'payeeIp',
  UpdateIp = 'updateIp',
  VirtualCardAccount = 'virtualCardAccount'
}

type ActionButtonType =
  | 'dashed'
  | 'default'
  | 'ghost'
  | 'link'
  | 'primary'
  | 'text';

type ButtonDisplayType = 'icon-only';

type Hook<T = { fields: ControlActionField[] }> = ({
  fields,
  instance
}: {
  fields: ControlActionField[];
  instance: unknown;
}) => T;

export interface ControlAction<
  HookType = Hook,
  BooleanHookType = Hook<boolean>,
  DisableHookType = never
> {
  groupKey?: string;
  name: string;
  entity: ActionableEntities;
  displayName?: string;
  type: ActionType;
  endpoint: string;
  fields?: (ControlActionField | ControlActionInfo)[];
  fieldsGroup?: ControlActionGroup;
  isSeparate?: boolean;
  parentDropDownName?: string;
  description?: string;
  buttonType?: ActionButtonType;
  icon?: string;
  color?: string;
  download?: boolean;
  hideFooter?: boolean;
  backgroundColor?: string;
  callbacks?: {
    onFinishAndClose?: boolean;
    onFinishAndOpen?: boolean;
    onFinishAndRefresh?: boolean;
    closeOnFinish?: boolean;
    refreshOnFinish?: boolean;
  };
  buttonDisplayType?: ButtonDisplayType;
  skipEntity?: boolean;
  skipShouldDisplayActionCheck?: boolean;
  hooks?: {
    load?: HookType;
    change?: Record<string, HookType>;
    shouldDisplayAction?: BooleanHookType;
    shouldDisableAction?: DisableHookType | DisableHookType[];
  };
  danger?: boolean;
  actionDrawerTitle?: string;
  showSuccessMessage?: boolean;
  keepOpenOnError?: boolean;
  showLoadingUntilCompleted?: boolean;
  drawerWidth?: string;
  showInvalidError?: boolean;
  translationKeys?: {
    submitButton?: string;
  };
  header?: {
    alert?: {
      type: 'info' | 'warning' | 'error' | 'success';
      message: string;
      dismissible?: boolean;
    };
  };
}

export interface Control {
  viewName: string;
  actions: ControlAction[];
}

export type ActionResponse = {
  message?: string;
};

export type WidgetChange = (
  fieldName: string,
  newValue:
    | FieldValue
    | FieldValue[]
    | Record<string, FieldValue | FieldValue[]>,
  index?: number
) => void;

export interface WidgetProps {
  field: ControlActionField;
  groupIndex?: number;
  onChange: WidgetChange;
  isFormLoading?: boolean;
  partnerName?: string;
}

export type FieldWidgetContainerProps = {
  field: ControlActionField;
  onChange: WidgetChange;
  isFormLoading?: boolean;
  partnerName?: string;
};

export enum CollapseTypes {
  KycLegalInformation = 'Legal Information',
  KycAddresses = 'Addresses',
  kycIndustryInformation = 'Industry Information',
  KycReferenceDBA = 'ReferenceDBA',
  KycReferenceLegalName = 'ReferenceLegalName',
  KycReferenceTaxId = 'ReferenceTaxId',
  KycReferenceTaxIdType = 'ReferenceTaxIdType',
  KycReferenceDOB = 'ReferenceDOB',
  KycReferenceBusinessType = 'ReferenceBusinessType',
  KycReferencePhone = 'ReferencePhone',
  KycReferenceCompanyWebsite = 'ReferenceCompanyWebsite',
  KycReferenceCompanyDescription = 'ReferenceCompanyDescription',
  KycReferenceBusinessZipCode = 'ReferenceBusinessZipCode',
  KycReferenceZipCode = 'ReferenceZipCode',
  KycReferenceSubIndustry = 'ReferenceSubIndustry'
}

export enum DirectionTypes {
  Horizontal = 'horizontal',
  Vertical = 'vertical'
}
